import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { getBotDeploymentIdsByBotIdSelector, getCurrentBotSelector } from '@common/reducers/ChatbotSelectors';
import { Button } from '@/components/ui/button';
import { DeploymentCard } from './DeploymentCard';
import { DeploymentCardSkeleton } from './DeploymentCardSkeleton';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

interface DeploymentsSidebarProps {
    botId?: string;
    selectedDeploymentId: string | null;
    onDeploymentSelect: (deploymentId: string | null) => void;
}

export const DeploymentsSidebar = memo(({ botId, selectedDeploymentId, onDeploymentSelect }: DeploymentsSidebarProps) => {
    const { t } = useTranslation(namespace);
    const isLoading = useAppSelector(state => state.DBBotDeploymentReducer.itemsLoading);
    const bot = useAppSelector(state => getCurrentBotSelector(state));

    const deploymentIds = useAppSelector(state => 
        botId ? getBotDeploymentIdsByBotIdSelector(state, botId) : []
    );
    
    const handleAllVersionsClick = useCallback(() => {
        onDeploymentSelect(null);
    }, [onDeploymentSelect]);

    const renderDeployments = useCallback(() => {
        if (isLoading) {
            return Array(3).fill(0).map((_, index) => (
                <DeploymentCardSkeleton key={index} />
            ));
        }

        if (deploymentIds.length === 0) {
            return (
                <div style={{
                    textAlign: 'center',
                    padding: '16px',
                    color: 'var(--deployments-sidebar-date-text)',
                }}>
                    {t('chatbot.botDetail.deployments.empty')}
                </div>
            );
        }

        return deploymentIds
            .filter((deploymentId): deploymentId is string => typeof deploymentId === 'string')
            .map(deploymentId => (
                <DeploymentCard
                    key={deploymentId}
                    deploymentId={deploymentId}
                    isSelected={selectedDeploymentId === deploymentId}
                    onSelect={() => onDeploymentSelect(deploymentId)}
                />
            ));
    }, [isLoading, deploymentIds, selectedDeploymentId, onDeploymentSelect]);

    return (
        <div className="flex flex-col h-full">
            <div style={{
                padding: '16px',
                borderBottom: '1px solid var(--deployments-sidebar-header-border)',
                backgroundColor: 'var(--deployments-sidebar-header-bg)',
            }}>
                <h2 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    marginBottom: '16px',
                    color: 'var(--deployments-sidebar-title)',
                }}>
                    {t('chatbot.botDetail.deployments.title')}
                </h2>
         
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-4">
                {renderDeployments()}
            </div>
        </div>
    );
}); 
